// .../Appointments/RecordTable/RecordTable.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Classes from './RecordTable.module.css';
import useScreenWidth from '../../../../../../../../../Hooks/useScreenWidth';
import SkeletonLoading from '../../../../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading';
import { Pagination, Stack } from '@mui/material';
import TableRow from './TableRow';
import { useNavigate } from 'react-router-dom';
import { AddIcon } from '../../../../../../../../../utils/Icons';

const DB_TO_UI = {
  completed: 'Completed',
  upcoming: 'Upcoming',
  cancelled: 'Cancelled',
  on_the_way: 'On the way',
  delayed: 'Delayed',
};

function formatDate(dateStr, timeSlot) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    const opts = { year: 'numeric', month: 'long', day: 'numeric' };
    const nice = d.toLocaleDateString(undefined, opts);
    return timeSlot ? `${nice} • ${timeSlot}` : nice;
  } catch {
    return dateStr;
  }
}

function formatAddress(loc = {}) {
  const parts = [loc.address1, loc.city, loc.region, loc.postalCode].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

export default function RecordTable({ loading, bookings = [], userId }) {
  const navigateTo = useNavigate();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;

  // derive rows from real bookings
  const rows = useMemo(() => {
    return (bookings || []).map(b => ({
      bookingId: b._id,
      serviceType: 'Fuel Delivery',
      serviceDate: formatDate(b?.appointment?.date, b?.appointment?.timeSlot),
      status: DB_TO_UI[b?.status] || b?.status || 'Upcoming',
      address: formatAddress(b?.location),
      raw: b,
    }));
  }, [bookings]);

  // local working copy so we can update row status after popup
  const [data, setData] = useState(rows);
  useEffect(() => setData(rows), [rows]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const totalPages = Math.max(1, Math.ceil((data?.length || 0) / itemsPerPage));
  const start = (currentPage - 1) * itemsPerPage;
  const currentItems = data.slice(start, start + itemsPerPage);
  const startItem = data.length ? start + 1 : 0;
  const endItem = Math.min(start + itemsPerPage, data.length);
  const totalItems = data.length;

  function handlePageChange(_, pageNum) { setCurrentPage(pageNum); }

  // update a row’s status label after successful save
  const handleStatusSaved = (bookingId, newLabel) => {
    setData(prev => prev.map(r => r.bookingId === bookingId ? { ...r, status: newLabel } : r));
  };

  // after cancel/delete, you can remove or mark as Cancelled. Here we mark as Cancelled.
  const handleCancelled = (bookingId) => {
    setData(prev => prev.map(r => r.bookingId === bookingId ? { ...r, status: 'Cancelled' } : r));
  };

  return (
    <div className={Classes.hero}>
      <div className={Classes.table}>
        <div className={Classes.tableWrapper}>

          <div className={Classes.tableHeader}>
            <div className={`${Classes.headerItem} ${Classes.header1}`}><h5>Service Type</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header2}`}><h5>Service Date</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header3}`}><h5>Status</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header4}`}><h5>Address</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header5}`}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className={Classes.addBtn} onClick={() => navigateTo(`/add-appointment/${userId}`)}>
                  <AddIcon height='12px' fill='white' />
                  <h5>Add Appt.</h5>
                </button>
              </div>
            </div>
          </div>

          <div className={Classes.tableBody}>
            {loading ? (
              <SkeletonLoading length={itemsPerPage} height={isMobile ? '3.5rem' : '2.8rem'} width="100%" br="6px" padding="0" />
            ) : currentItems && currentItems.length > 0 ? (
              currentItems.map((row, idx) => (
                <TableRow
                  key={row.bookingId}
                  value={row}
                  index={idx}
                  onStatusSaved={handleStatusSaved}
                  onCancelled={handleCancelled}
                />
              ))
            ) : (
              <h5 className='placeholderText' style={{ margin: '10px 19px' }} >No appointments yet...</h5>
            )}
          </div>

          {currentItems && currentItems.length > 0 &&
            <div className={Classes.pagFooter}>
              <h5 style={{ height: '1.2rem' }}>
                {loading
                  ? <SkeletonLoading height='1.2rem' width='10rem' br='4px' padding='0' />
                  : `Showing ${startItem} to ${endItem} of ${totalItems} results`}
              </h5>
              <Stack spacing={2} className={Classes.pagination}>
                {loading
                  ? <SkeletonLoading height='1.2rem' width='5rem' br='4px' padding='0' />
                  : <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} shape="rounded" />}
              </Stack>
            </div>
          }

        </div>
      </div>
    </div>
  );
}
